import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AdminHTTPService } from './admin-http/admin-http.service';
import { AdminModel } from '../models/admin.model';
import { UserModel } from '../models/admin-user.model';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AdminService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  adminLocalStorageToken = `AdminLoginToken`;

  // public fields
  currentUser$: Observable<UserType>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserType>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AdminHTTPService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserType>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();

    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(emailId: string, password: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(emailId, password).pipe(
      map((auth: AdminModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        // console.log(result);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      // catchError((err) => {
      //   // return of(undefined);
      //   // console.error('err', err);
      //   // this.changeError = err;
      //   return of(err);
      // }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    localStorage.removeItem(this.adminLocalStorageToken);
    localStorage.clear();
    this.router.navigate(['/admin/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserType> {
    const auth = this.getAuthFromLocalStorage();
    if (!auth || !auth.token) {
      return of(undefined);
    } else {
    }
    this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.token).pipe(
      map((user: UserType) => {
        if (user) {
          this.currentUserSubject.next(user);
          // console.log(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  // registration(user: UserModel): Observable<any> {
  //   this.isLoadingSubject.next(true);
  //   return this.authHttpService.createUser(user).pipe(
  //     map(() => {
  //       this.isLoadingSubject.next(false);
  //     }),
  //     switchMap(() => this.login(user.emailId, user.password)),
  //     catchError((err) => {
  //       console.error('err', err);
  //       return of(undefined);
  //     }),
  //     finalize(() => this.isLoadingSubject.next(false))
  //   );
  // }

  forgotPassword(email: string, sendOtpTo: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email, sendOtpTo)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }
  otp(otp: string, email: string, password: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .OTP(otp, email, password)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  ADMIN = 'admin';
  // private methods
  private setAuthFromLocalStorage(auth: AdminModel): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.token) {
      localStorage.setItem(this.adminLocalStorageToken, JSON.stringify(auth));
      localStorage.setItem(this.ADMIN, JSON.stringify(this.ADMIN));
      // console.log(auth);
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AdminModel | undefined {
    try {
      const lsValue = localStorage.getItem(this.adminLocalStorageToken);
      // console.log(lsValue);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      // console.log(authData.token);
      return authData;
    } catch (error) {
      // console.error(error);
      return undefined;
    }
  }
  getToken() {
    try {
      const lsValue = localStorage.getItem(this.adminLocalStorageToken);
      // console.log(lsValue);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData.token;
    } catch (error) {
      // console.error(error);
      return undefined;
    }
  }

  getRefreshToken() {
    try {
      const lsValue = localStorage.getItem(this.adminLocalStorageToken);
      // console.log(lsValue);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      // console.log(authData.refreshToken);
      return authData.refreshToken;
    } catch (error) {
      // console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
